import React from "react";
import { Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { Campaign } from "@/hooks/useCampaigns";
import { formatDateRange } from "../lib/formatters";
import { BrandLogo } from "./BrandLogo";

interface CampaignSelectionListProps {
  campaigns: Campaign[];
  campaignsLoading: boolean;
  selectedCampaignId: string;
  onSelectCampaign: (id: string) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export const CampaignSelectionList: React.FC<CampaignSelectionListProps> = ({
  campaigns,
  campaignsLoading,
  selectedCampaignId,
  onSelectCampaign,
  searchQuery,
  onSearchQueryChange,
}) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-1">
          Select Campaign <span className="text-red-500">*</span>
        </h3>
      </div>

      {/* Search input */}
      <div className="relative mb-2.5">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          placeholder="Search campaigns..."
          className="pl-9 h-10 rounded-xl border-slate-200 text-sm focus-visible:ring-[#A655F8] focus-visible:border-[#A655F8]"
        />
      </div>

      {/* Scrollable Campaign List without visible scrollbar */}
      <div className="space-y-2.5 max-h-[370px] overflow-y-auto no-scrollbar">
        {campaignsLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Loading campaigns...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-slate-200 rounded-2xl text-slate-400 text-sm">
            {searchQuery
              ? "No matching campaigns found."
              : "No campaigns available."}
          </div>
        ) : (
          campaigns.map((camp: Campaign) => {
            const isSelected = selectedCampaignId === camp.id;
            const dateDisplay = formatDateRange(
              camp.start_date,
              camp.end_date || camp.deadline
            );
            const statusStr = (camp.status || "draft").toLowerCase();

            let statusBadgeClass =
              "bg-slate-100 text-slate-600 border-slate-200";
            if (statusStr === "active") {
              statusBadgeClass =
                "bg-blue-50 text-blue-600 border-blue-200/80";
            } else if (statusStr === "completed") {
              statusBadgeClass =
                "bg-emerald-50 text-emerald-600 border-emerald-200/80";
            } else if (statusStr === "paused") {
              statusBadgeClass =
                "bg-amber-50 text-amber-600 border-amber-200/80";
            }

            return (
              <div
                key={camp.id}
                onClick={() => onSelectCampaign(camp.id)}
                className={`p-3 rounded-2xl border transition-all flex items-center justify-between cursor-pointer ${
                  isSelected
                    ? "border-2 border-[#A655F8] bg-[#FAF5FF]/30 shadow-xs"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
                }`}
              >
                {/* Left: Brand Logo & Campaign Title */}
                <div className="flex items-center gap-3 min-w-0 pr-2">
                  <BrandLogo
                    logoUrl={camp.brand_logo}
                    name={camp.brand_name || camp.brand || camp.name}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">
                      {camp.name}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {dateDisplay}
                    </p>
                  </div>
                </div>

                {/* Right: Status badge & Radio selection indicator */}
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border capitalize ${statusBadgeClass}`}
                  >
                    {camp.status || "Draft"}
                  </span>

                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-[#A655F8] text-white shadow-xs"
                        : "border-2 border-slate-300"
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
